import { Coins01 } from '@untitledui/icons';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import type { FullEnrichCreditsUsage } from '@/lib/billing/creditTypes';
import { useCreditBalance } from '@/lib/billing/useCreditBalance';
import { useCreditCheckout } from '@/lib/billing/useCreditCheckout';

interface CreditsSectionProps {
  orgId: number;
}

function BalanceRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex items-center justify-between'>
      <span className='text-body-small text-tertiary'>{label}</span>
      <span className='text-body text-secondary'>{value}</span>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className='animate-pulse space-y-3'>
      <div className='flex items-center justify-between'>
        <div className='h-3 w-24 rounded bg-base-300' />
        <div className='h-3 w-16 rounded bg-base-300' />
      </div>
      <div className='flex items-center justify-between'>
        <div className='h-3 w-32 rounded bg-base-300' />
        <div className='h-3 w-12 rounded bg-base-300' />
      </div>
      <div className='flex items-center justify-between'>
        <div className='h-3 w-28 rounded bg-base-300' />
        <div className='h-3 w-12 rounded bg-base-300' />
      </div>
    </div>
  );
}

function CreditBalanceContent({
  balance,
  isLoading,
  isError,
}: {
  balance: FullEnrichCreditsUsage | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isError && !balance) {
    return (
      <p className='text-body text-tertiary'>Credit balance unavailable</p>
    );
  }
  if (isLoading && !balance) {
    return <SkeletonRows />;
  }
  if (!balance) {
    return null;
  }
  return (
    <div className='space-y-3'>
      <BalanceRow
        label='Monthly Usage'
        value={`${balance.monthly_used} / ${balance.monthly_limit}`}
      />
      <BalanceRow
        label='Purchased Balance'
        value={String(balance.purchased_balance)}
      />
      <BalanceRow label='Total Available' value={String(balance.available)} />
      {balance.overage_count > 0 && (
        <div className='flex items-center justify-between'>
          <span className='text-body-small text-tertiary'>Overage</span>
          <span className='text-body text-[var(--color-text-warning-primary)]'>
            {balance.overage_count}
          </span>
        </div>
      )}
    </div>
  );
}

export function CreditsSection({ orgId }: CreditsSectionProps) {
  const { data: balance, isLoading, isError } = useCreditBalance(orgId);
  const {
    buyCredits,
    isPending,
    isUnavailable,
    isError: checkoutError,
  } = useCreditCheckout(orgId);

  if (!balance?.enforcement_enabled) {
    return null;
  }

  return (
    <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
      <div className='px-6 py-5 border-b border-border-secondary'>
        <div className='flex items-center gap-3'>
          <FeaturedIcon icon={Coins01} size='sm' theme='modern' color='gray' />
          <div>
            <h2 className='text-lg font-semibold text-secondary'>Credits</h2>
            <p className='text-sm text-tertiary mt-0.5'>
              Used for phone and contact enrichment
            </p>
          </div>
        </div>
      </div>

      <div className='p-6'>
        <CreditBalanceContent
          balance={balance}
          isLoading={isLoading}
          isError={isError}
        />

        <div className='mt-6 pt-6 border-t border-border-secondary'>
          {isUnavailable ? (
            <div className='flex items-center gap-3'>
              <Button color='secondary' size='sm' isDisabled>
                Buy credits
              </Button>
              <span className='text-tertiary text-body-small'>
                Credit packs coming soon
              </span>
            </div>
          ) : (
            <div className='flex items-center gap-3'>
              <Button
                color='secondary'
                size='sm'
                onClick={buyCredits}
                isLoading={isPending}
                isDisabled={isPending}
              >
                Buy credits
              </Button>
              {checkoutError && (
                <span className='text-[var(--color-text-error-primary)] text-body-small'>
                  Couldn&apos;t start checkout. Please try again.
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
